import React, { useState, useEffect } from "react";
import { Select, Drawer, Modal } from "antd";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import { toast } from "react-toastify";
import { SetHeading } from "../../context/NavbarContext";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import i18next from "i18next";
import { URLS } from "../../API/API endpoints"
import {

  getBillPaymentHistory,
  getPurchaseList,
  getVendors,
} from "../../API  Functions/PurchaseFunctions";
import axiosInstance from "../../AxiosInstance"
import moment from "moment";
import PurchasePayment from "../../modals/purchasePaymentModal";
import SearchBox from "../../components/CustomWidgets/SearchBox";
import SelectBox from "../../components/CustomWidgets/EOrderFilter";
import VendorPaymentTable from "./VendorPaymentTable";
import ShimmerLoader from "../../loading";
import DateFilter from "../../components/ui/DateFilter";
import dayjs from "dayjs";
import VendorSummaryTile from "./VendorSummaryTile"
import { checkPermission } from "../../Utils/permissionHelper";
import { store } from "../../Redux/store"


const { Option } = Select;

const today = dayjs();
const formatToday = today.format("YYYY-MM-DD");
const startofMonth = today.startOf("month").format("YYYY-MM-DD");


/**
 * Flattens getPurchaseList's nested structure into the flat shape
 * VendorPaymentTable expects, while keeping _raw for row-click navigation.
 */
const flattenRow = (val) => {
  const od = val?.order_details ?? {};
  return {
    // ── original vendor-payment fields ──────────────────────────────────────
    order_no: od.order_no ?? "",
    supplier_name: od.partner_name ?? "",
    supplier_id: od.partner_id ?? null,
    order_date: od.order_date ?? "",
    vendor_reference: od.venfor_ref ?? "",
    invoice_value: od.total_amount ?? 0,
    paid_amount: od.paid_amount ?? 0,
    due_amount: od.due_amount ?? 0,
    store_name: od.store_name ?? "",
    bill_id: od.bill_id ?? null,
    // ── new fields from purchase history ────────────────────────────────────
    status: od.status ?? "",
    payment_status: od.payment_status ?? "",
    create_date: od.create_date ?? "",
    due_date: od.due_date ?? "",
    payment_term: od.payment_term ?? "",
    quantity: od.total_quantity ?? "",
    // ── raw original kept so row-click can pass full orderData ───────────────
    _raw: val,
  };
};

const SupplyPayment = () => {
  const { t } = useTranslation();
  const location = useLocation();

  const user_id = useSelector((state) => state?.login?.user?.result?.user_id);
  const isGinne = useSelector((state) => state?.geneLoginReducer?.isGinneLogin);
  const [pageIndex, setPageIndex] = useState(1); // useState returns an array — fine here


  const fromPage = location.state?.from;
  const orderIdToOpen = location.state?.orderId;

  // ── state ──────────────────────────────────────────────────────────────────
  const [popup, setPopup] = useState(false);
  const [selectedBillId, setSelectedBillId] = useState(null);
  const [selectedDueAmount, setSelectedDueAmount] = useState(null);
  const [visible, setVisible] = useState(false);
  const [paymentHistoryData, setPaymentHistoryData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [selectedPaymentType, setSelectedPaymentType] = useState(0);
  const [data, setData] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [vendorLoader, setVendorLoading] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState("");
  const [selectedVendorID, setSelectedVendorID] = useState("");
  const [startDate, setStartDate] = useState(startofMonth);
  const [endDate, setEndDate] = useState(formatToday);
  const [selectedRange, setSelectedRange] = useState("thisMonth");
  const [currentLimit, setCurrentLimit] = useState(10);
  const [currentOffset, setCurrentOffset] = useState(0);
  const [isSelectMode, setIsSelectMode] = useState(false);
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [deleteOrdersLoading, setDeleteOrdersLoading] = useState(false);

  // ── filter options (matching PurchaseList) ─────────────────────────────────
  const filterOptions = [
    { value: 0, label: t("all_orders") },
    { value: 1, label: t("Draft Orders") },
    { value: 2, label: t("Purchase Orders") },
  ];

  const toStateParam = (n) => {
    if (Number(n) === 1) return "draft";
    if (Number(n) === 2) return "done";
    return "";
  };

  // ── fetch ──────────────────────────────────────────────────────────────────
  const fetchData = (
    search = "",
    paymentType = 0,
    offset = 0,
    start = startDate,
    end = endDate,
    vendorID = selectedVendorID || null
  ) => {
    if (search) setIsSearching(true);
    else setLoading(true);

    const payload = {
      lang: i18next.language === "ar" ? "ar_001" : "en_US",
      user_id,
      state: toStateParam(paymentType),
      order_no: search,
      limit: currentLimit,
      offset,
      start_date: start || "",
      end_date: end || "",
      ...(vendorID && { vendor_id: vendorID }),
    };

    getPurchaseList(payload)
      .then((res) => {
        if (res?.result?.[0]?.status_code === 200) {
          setData(res.result[0].response.map(flattenRow));
        } else {
          setData([]);
        }
        setIsSearching(false);
        setLoading(false)
      })
      .catch((err) => {
        console.error("Error fetching purchase list:", err);
        setIsSearching(false);
        setLoading(false);
      });
  };

  // ── payment history drawer ─────────────────────────────────────────────────
  const showDrawer = (row) => {
    setVisible(true);
    getBillPaymentHistory({ bill_id: row.bill_id })
      .then((res) => {
        setPaymentHistoryData(
          res?.result?.payments?.length > 0 ? res.result.payments : []
        );
      })
      .catch((err) => console.error(err));
  };

  const onClose = () => setVisible(false);

  const getOrderId = (row) => row?._raw?.order_details?.order_id;

  const toggleSelectOrder = (id) => {
    setSelectedOrders((prev) =>
      prev.includes(id) ? prev.filter((orderId) => orderId !== id) : [...prev, id],
    );
  };

  const handleSelectAllOrders = (checked) => {
    if (checked) {
      setSelectedOrders(
        (data || []).map((row) => getOrderId(row)).filter((id) => id != null),
      );
    } else {
      setSelectedOrders([]);
    }
  };

  const handleExitSelectMode = () => {
    setIsSelectMode(false);
    setSelectedOrders([]);
  };

  const handleDeleteSelectedOrders = () => {
    Modal.confirm({
      title: t("Are you sure you want to delete the selected orders?"),
      icon: <ExclamationCircleOutlined style={{ color: "#faad14" }} />,
      okText: t("Yes"),
      cancelText: t("No"),
      okButtonProps: {
        className: "bg-primaryBlue hover:bg-primaryBlue text-white border-none",
      },
      onOk: async () => {
        try {
          setDeleteOrdersLoading(true);
          const payload = {
            order_ids: selectedOrders,
            user_id: user_id,
          };
          const response = await axiosInstance.post(
            URLS.DELETE_PURCHASE_ORDER,
            payload,
          );
          if (response?.data?.result?.status_code == 200) {
            toast.success(t(response?.data?.result?.status));
            handleExitSelectMode();
            fetchData(
              searchKeyword,
              selectedPaymentType,
              currentOffset,
              startDate,
              endDate,
              selectedVendorID || null,
            );
          } else {
            toast.error(t(response?.data?.result?.status));
          }
        } catch (error) {
          console.error("Error deleting purchase orders:", error);
        } finally {
          setDeleteOrdersLoading(false);
        }
      },
    });
  };

  // ── vendors ────────────────────────────────────────────────────────────────
  const getVendorsData = () => {
    setVendorLoading(true);
    getVendors(user_id).then((res) => {
      setVendors(res);
      setVendorLoading(false);
    });
  };
  const { login } = store.getState();


  const company_id = login?.user?.result?.company_id


  // ── effects ────────────────────────────────────────────────────────────────
  useEffect(() => {
    getVendorsData();
    // fetchData("", 0, 0, startDate, endDate);
  }, [popup]);

  useEffect(() => {
    // fetchData("", 0, 0, startDate, endDate);
  }, []);

  useEffect(() => {
    fetchData(
      searchKeyword,
      selectedPaymentType,
      currentOffset,
      startDate,
      endDate,
      selectedVendorID || null
    );
  }, [currentLimit, currentOffset]);


    useEffect(() => {
    fetchVendorSummaryMetrics(
      selectedPaymentType,
       startDate,
      endDate,
      selectedVendorID || null
    )
  }, [])

  useEffect(() => {
    if (fromPage === "vendor_payment" && orderIdToOpen) {
      fetchData(orderIdToOpen, 0, 0, startofMonth, formatToday);
    }
  }, [fromPage, orderIdToOpen]);

  // ── handlers ───────────────────────────────────────────────────────────────
  const handlePaymentClick = (row) => {
    if (!checkPermission("/purchase/supplyPayment", isGinne)) return;
    setSelectedBillId(row.bill_id);
    setSelectedDueAmount(row.due_amount);
    setIsModalVisible(true);
  };

  const handleSearchChange = (value) => {
    setSearchKeyword(value);
    fetchData(value, selectedPaymentType, 0, startDate, endDate, selectedVendorID || null);
  };

  const handlePaymentTypeChange = (event) => {
    const paymentType = Number(event.target.value);
    setSelectedPaymentType(paymentType);
    fetchData(searchKeyword, paymentType, 0, startDate, endDate, selectedVendorID || null);
    fetchVendorSummaryMetrics(paymentType,startDate, endDate, selectedVendorID || null)
  };

  const handleSelectVendor = (val) => {
    if (val === "All Vendors") {
      setSelectedVendorID(null);
      setSelectedVendor("");
      fetchData(searchKeyword, selectedPaymentType, 0, startDate, endDate, null);
    fetchVendorSummaryMetrics(selectedPaymentType,startDate, endDate, null)
      return;
    }
    setSelectedVendorID(val);
    setSelectedVendor(vendors?.find((v) => v.id === val)?.name ?? "");
    fetchData(searchKeyword, selectedPaymentType, 0, startDate, endDate, val);
    fetchVendorSummaryMetrics(selectedPaymentType,startDate, endDate, val)
  };

  const handleDateFilter = (start, end) => {
    setSearchKeyword("");
    setStartDate(start);
    setEndDate(end);
    setCurrentLimit(10);
    setCurrentOffset(0);
    fetchData("", selectedPaymentType, 0, start, end, selectedVendorID || null);
    fetchVendorSummaryMetrics(selectedPaymentType,start, end, selectedVendorID || null)
  };

  console.log("DATA=>", data)
  const [summaryMetrics, setSummaryMetrics] = useState(null)

  const fetchVendorSummaryMetrics = async (state, start_date, end_date, vendor_id) => {
    try {
      console.log(company_id);

      const payload = {
        company_id,
        state: toStateParam(state),
      start_date: start_date,
      end_date: end_date,
      vendor_id:vendor_id
      };

      const res = await axiosInstance.post(
        URLS.GET_PURCHASE_INFO,
        payload
      );

      setSummaryMetrics(res?.data?.result?.response);
    } catch (error) {
      console.error("Error fetching vendor summary metrics:", error);

     
    }
  };



  // ── render ─────────────────────────────────────────────────────────────────
  return (
    <>
      <div className="w-full flex flex-col bg-[#F6F7F9] min-h-[93vh]">
        <SetHeading value="Vendor Invoices" />

        <div className="px-4 font-extrabold overflow-y-auto overflow-hidden custom-scrollbar">

          {/* Optimized Top Bar Structure */}
          <div className="flex flex-row justify-between items-center mt-4 mb-4 gap-4 flex-wrap">
            <div className="flex-1 min-w-[280px] max-w-md relative top-1">
              <SearchBox
                placeholder={t("Search Vendor/Order No")}
                searchResult={handleSearchChange}
                loading={isSearching}
              />
            </div>

            <div className="flex flex-row items-center gap-3 flex-wrap ">
              <Select
                className="overflow-hidden relative top-1"
                style={{ width: 200, minWidth: 200, height: 40 }}
                placeholder={
                  vendorLoader ? (
                    <span className="flex items-center gap-2 text-gray-400">
                      <span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-gray-300 border-t-blue-500" />
                      {t("Loading vendors...")}
                    </span>
                  ) : t("Select Vendor")
                }
                value={selectedVendor || "All Vendors"}
                onChange={handleSelectVendor}
                disabled={vendorLoader}
              >
                <Option value="All Vendors">{t("All Vendors")}</Option>
                {vendors?.map(({ id, name, phone, email }) => {
                  let label = name;
                  if (phone) label += ` (${phone})`;
                  else if (email) label += ` (${email})`;
                  return <Option key={id} value={id}>{label}</Option>;
                })}
              </Select>

              {/* Stabilized width to prevent layout skewing */}
              <div style={{ width: 160, minWidth: 160 }}>
                <SelectBox
                  style={{ width: "100%", height: 40 }}
                  width={160}
                  placeholder={t("all_orders")}
                  name="filter"
                  onChange={handlePaymentTypeChange}
                  options={filterOptions}
                  value={selectedPaymentType}
                />
              </div>

              <DateFilter
                setSelectedRange={setSelectedRange}
                selectedRange={selectedRange}
                SubmitFilter={handleDateFilter}
              />
            </div>
          </div>

          <VendorSummaryTile data={summaryMetrics} />
{data?.length > 0 && 
 <div className="flex justify-end">
            {!isSelectMode ? (
              <button
                onClick={() => setIsSelectMode(true)}
                className="flex h-10 w-full shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-lg border border-primaryBlue px-4 text-sm font-semibold text-primaryBlue sm:w-auto"
              >
                {t("Select Orders")}
              </button>
            ) : (
              <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center">
                <span className="whitespace-nowrap text-sm text-gray-500 italic">
                  {selectedOrders.length > 0
                    ? `${selectedOrders.length} ${t("order(s) selected")}`
                    : t("Select orders to delete")}
                </span>
                <div className="flex shrink-0 gap-2">
                  <button
                    onClick={handleExitSelectMode}
                    className="h-10 shrink-0 whitespace-nowrap rounded-lg border border-gray-300 bg-white px-3 text-sm font-medium text-gray-600"
                  >
                    {t("Cancel")}
                  </button>
                  <button
                    disabled={selectedOrders.length === 0 || deleteOrdersLoading}
                    onClick={handleDeleteSelectedOrders}
                    className={`flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg px-3 h-10 text-white text-sm font-semibold transition-opacity ${
                      selectedOrders.length === 0
                        ? "bg-[#D10000] opacity-40 cursor-not-allowed"
                        : "bg-[#D10000] hover:bg-[#e01414]"
                    }`}
                  >
                    {deleteOrdersLoading ? t("Deleting...") : t("Delete Selected")}
                    {selectedOrders.length > 0 && (
                      <span className="bg-white text-[#D10000] text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                        {selectedOrders.length}
                      </span>
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>}
         

          {/* table */}
          {loading ? (
            <ShimmerLoader />
          ) : (
            <div className="overflow-x-auto w-full">
              <VendorPaymentTable
                handlePaymentClick={handlePaymentClick}
                showDrawer={showDrawer}
                additionalData={data}
                pageIndex={pageIndex}
                setPageIndex={setPageIndex}
                pagination={(values) => {
                  setCurrentLimit(values.limit);
                  setCurrentOffset(values.offset);
                }}
                showPaymenthistory={true}
                isSelectMode={isSelectMode}
                selectedOrders={selectedOrders}
                onToggleSelect={toggleSelectOrder}
                onSelectAll={handleSelectAllOrders}
              />
            </div>
          )}
        </div>
      </div>

      {/* payment modal */}
      {isModalVisible && (
        <PurchasePayment
          isOpen={isModalVisible}
          onClose={() => setIsModalVisible(false)}
          bill_id={selectedBillId}
          page="supplyPayment"
          due_amount={selectedDueAmount}
          Popup={() => setPopup(true)}
        />
      )}

      {/* payment history drawer */}
      <Drawer
        title={t("Payment History")}
        placement="right"
        width={500}
        onClose={onClose}
        visible={visible}
      >
        {paymentHistoryData.length > 0 ? (
          <table className="w-full">
            <thead>
              <tr className="bg-gray-200">
                <th className="p-2 text-start">{t("Payment Date & Time")}</th>
                <th className="p-2 text-start">{t("Type")}</th>
                <th className="p-2 text-start">{t("amount")}</th>
              </tr>
            </thead>
            <tbody>
              {paymentHistoryData.map((payment, index) => (
                <tr key={index} className="border-b">
                  <td className="p-2">
                    {moment(payment.payment_date).format("YYYY-MM-DD HH:mm:ss")}
                  </td>
                  <td className="p-2">{t(payment.type)}</td>
                  <td className="p-2">EGP {payment.amount}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p>{t("No payment history available")}</p>
        )}
      </Drawer>
    </>
  );
};

export default SupplyPayment;